/**
 * @module dynamic/awe-library/runtime/records
 */
import { initialize } from 'common/offline-data-service/behaviour-cache'
import { updateDocumentsLive } from 'dynamic/awe-library/runtime/lib/get-instance-controller'
import useAsync from 'common/use-async'
import { get } from 'common/offline-data-service/functions/get'
import { list } from 'common/offline-data-service/functions/list'

/**
 * @typedef RecordResults
 * @type {array}
 * @global
 * @property {Array<Document>} 0 - the items retrieved
 * @property {Array<FieldDefinition>} 1 - the field definitions
 */

/**
 * A function to retrieve records from a type.  The documents
 * retrieved are automatically updated by replaying local
 * changes.
 *
 * @param {string|DocumentDefinition} type - the type whose records should be retrieved
 * @param {WhereDefinition} [query] - a query to apply
 * @param {number} [skip] - the number of records to skip
 * @param {number} [take] - the number of records to return
 * @param {ListOptions} [options] - options to provide
 * @returns {Promise<RecordResults>} a promise for the matching records and field definitions in the type
 */
export async function getRecords(type, query, skip, take, options, updateMethod = updateDocumentsLive) {
    if (!type) return []
    let lookupType = await getType(type)
    await initialize(lookupType)
    let fields = lookupType.sendMessage('fields', [])
    const where = Object.assign({}, query)
    let items = (
        await list(lookupType.database, lookupType.table, where, {
            ...options,
            skip,
            take,
        })
    ).map((d) => d.data)
    items.loaded = true
    await updateMethod(items)
    return [items, fields]
}

/**
 * A hook function to retrieve records from a type.  The documents
 * retrieved are automatically updated by replaying local
 * changes. Returns [[], []] until valid results are available
 *
 * @param {string|DocumentDefinition} type - the type whose records should be retrieved
 * @param {WhereDefinition} [query] - a query to apply
 * @param {number} [skip] - the number of records to skip
 * @param {number} [take] - the number of records to return
 * @param {ListOptions} [options] - options to provide
 * @returns {RecordResults} The records and field definitions
 */ export function useRecordsOfType(type, query, skip, take, options) {
    return useAsync(
        async () => {
            return await getRecords(type, query, skip, take, options)
        },
        [[], []],
        `${type}-${skip}:${take}!${JSON.stringify(query)}`
    )
}

/**
 * Retrieves a type - we always prefer the server for types
 * @param {string} id - the id of the type
 * @param {boolean} [fromServer] - whether we should retrieve the type from the server
 * @returns {Promise<DocumentDefinition>} a promise for the definition
 */
export async function getType(id, fromServer) {
    if (Object.isObject(id)) return id
    if (!id) return null
    return await get(id, true, false, fromServer ? 'server-preferred' : undefined)
}
